import { useMemo, useState } from 'react';
import styles from './Calculator.module.css';

type DesignType = 'bca' | 'incomplete' | 'lattice';

interface LayoutBlock {
  title: string;
  plots: string[];
  columns: number;
}

interface DesignerResult {
  totalPlots: number;
  numBlocks: number;
  power: number;
  dfError: number;
  layoutBlocks: LayoutBlock[];
  interpretation: string;
}

const calculatePower = (reps: number, cv: number, diff: number): number => {
  const mse = Math.pow(cv / 100, 2);
  const ncp = Math.pow(diff / 100, 2) * reps / (2 * mse);
  const power = 1 - Math.exp(-ncp / 2);
  return Math.min(0.99, Math.max(0.05, power));
};

const shuffle = <T,>(array: T[]): T[] => {
  const arr = [...array];
  for (let i = arr.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
};

const getDesignName = (designType: DesignType): string => {
  if (designType === 'bca') return 'Bloques Completos al Azar (BCA)';
  if (designType === 'incomplete') return 'Bloques Incompletos Balanceados';
  return 'Latice Cuadrado';
};

export const ExperimentDesigner: React.FC = () => {
  const [designType, setDesignType] = useState<DesignType>('bca');
  const [treatments, setTreatments] = useState<string>('12');
  const [reps, setReps] = useState<string>('3');
  const [cv, setCv] = useState<string>('15');
  const [diff, setDiff] = useState<string>('10');
  const [alpha, setAlpha] = useState<string>('0.05');
  const [error, setError] = useState<string>('');
  const [result, setResult] = useState<DesignerResult | null>(null);

  const generateLayout = (
    selectedDesignType: DesignType,
    treatmentsNum: number,
    repsNum: number
  ): { blocks: LayoutBlock[]; numBlocks: number } => {
    const labels = Array.from({ length: treatmentsNum }, (_, i) => `T${i + 1}`);

    if (selectedDesignType === 'bca') {
      const blocks = Array.from({ length: repsNum }, (_, i) => {
        const shuffled = shuffle(labels);
        return {
          title: `Bloque ${i + 1}`,
          plots: shuffled,
          columns: Math.ceil(Math.sqrt(treatmentsNum)),
        };
      });
      return { blocks, numBlocks: repsNum };
    }

    if (selectedDesignType === 'incomplete') {
      const blockSize = Math.max(3, Math.floor(Math.sqrt(treatmentsNum)));
      const totalPlots = treatmentsNum * repsNum;
      const numBlocks = Math.ceil(totalPlots / blockSize);
      let used = 0;
      const blocks: LayoutBlock[] = [];

      for (let block = 1; block <= numBlocks; block += 1) {
        const shuffled = shuffle(labels);
        const plots: string[] = [];
        for (let i = 0; i < blockSize && used < totalPlots; i += 1) {
          plots.push(shuffled[i % labels.length]);
          used += 1;
        }
        blocks.push({
          title: `Bloque Incompleto ${block}`,
          plots,
          columns: blockSize,
        });
      }
      return { blocks, numBlocks };
    }

    const k = Math.ceil(Math.sqrt(treatmentsNum));
    const actualTreatments = k * k;
    const latticeLabels = Array.from({ length: actualTreatments }, (_, i) =>
      i < treatmentsNum ? `T${i + 1}` : `F${i - treatmentsNum + 1}`
    );
    const blocks = Array.from({ length: repsNum }, (_, i) => ({
      title: `Repeticion ${i + 1} - Latice ${k}x${k}`,
      plots: shuffle(latticeLabels),
      columns: k,
    }));

    return { blocks, numBlocks: repsNum };
  };

  const buildInterpretation = (
    selectedDesignType: DesignType,
    treatmentsNum: number,
    repsNum: number,
    power: number,
    cvNum: number
  ): string => {
    let text = `Diseno seleccionado: ${getDesignName(selectedDesignType)}. `;
    text += `Con ${treatmentsNum} tratamientos y ${repsNum} repeticiones, la potencia estimada es ${(power * 100).toFixed(1)}%. `;

    if (power >= 0.8) {
      text += 'Potencia adecuada (>= 80%) para detectar las diferencias definidas. ';
    } else {
      const repsNeeded = Math.ceil(repsNum / (power / 0.8));
      text += `Potencia baja. Se sugiere aumentar a ~${repsNeeded} repeticiones. `;
    }

    if (cvNum > 20) {
      text += `El CV esperado (${cvNum}%) es alto; conviene mejorar homogeneidad del sitio experimental. `;
    }

    if (selectedDesignType === 'bca') {
      text += 'BCA: simple de analizar y recomendable con bloques bien definidos.';
    } else if (selectedDesignType === 'incomplete') {
      text += 'Bloques incompletos: utiles con muchos tratamientos y heterogeneidad espacial.';
    } else {
      text += 'Latice: alta eficiencia para gran numero de tratamientos.';
    }

    return text;
  };

  const handleGenerate = (): void => {
    const treatmentsNum = Number(treatments);
    const repsNum = Number(reps);
    const cvNum = Number(cv);
    const diffNum = Number(diff);
    const alphaNum = Number(alpha);
    setError('');

    if (
      Number.isNaN(treatmentsNum) ||
      Number.isNaN(repsNum) ||
      Number.isNaN(cvNum) ||
      Number.isNaN(diffNum) ||
      Number.isNaN(alphaNum) ||
      treatmentsNum < 2 ||
      repsNum < 2
    ) {
      setResult(null);
      setError('Ingrese parametros validos. Tratamientos y repeticiones deben ser al menos 2.');
      return;
    }

    const totalPlots = treatmentsNum * repsNum;
    const dfError = (treatmentsNum - 1) * (repsNum - 1);
    const power = calculatePower(repsNum, cvNum, diffNum);
    const layoutInfo = generateLayout(designType, treatmentsNum, repsNum);
    const interpretation = buildInterpretation(designType, treatmentsNum, repsNum, power, cvNum);

    setResult({
      totalPlots,
      numBlocks: layoutInfo.numBlocks,
      power,
      dfError,
      layoutBlocks: layoutInfo.blocks,
      interpretation,
    });
  };

  const powerCurve = useMemo(() => {
    const treatmentsNum = Number(treatments);
    const cvNum = Number(cv);
    const diffNum = Number(diff);
    const alphaNum = Number(alpha);
    if (
      Number.isNaN(treatmentsNum) ||
      Number.isNaN(cvNum) ||
      Number.isNaN(diffNum) ||
      Number.isNaN(alphaNum) ||
      treatmentsNum < 2
    ) {
      return null;
    }

    const width = 920;
    const height = 320;
    const padLeft = 56;
    const padRight = 20;
    const padTop = 24;
    const padBottom = 42;
    const innerWidth = width - padLeft - padRight;
    const innerHeight = height - padTop - padBottom;

    const points = Array.from({ length: 9 }, (_, i) => {
      const r = i + 2;
      const p = calculatePower(r, cvNum, diffNum);
      return { r, p };
    });

    const x = (rep: number): number => padLeft + ((rep - 2) / 8) * innerWidth;
    const y = (powerValue: number): number => padTop + (1 - powerValue) * innerHeight;

    const path = points
      .map((point, idx) => `${idx === 0 ? 'M' : 'L'} ${x(point.r)} ${y(point.p)}`)
      .join(' ');
    const y80 = y(0.8);

    return { width, height, padLeft, padRight, padBottom, points, x, y, path, y80 };
  }, [treatments, cv, diff, alpha]);

  return (
    <div className={styles.calculatorContainer}>
      <div className={styles.designerBox}>
        <div className={styles.calculatorHeader}>
          <h2>Disenador de Experimentos</h2>
          <p>
            Genere disenos experimentales para ensayos de mejoramiento con croquis y potencia
            estadistica.
          </p>
        </div>

        <div className={styles.inputSection}>
          <h3>Parametros del diseno</h3>
          <div className={styles.inputRow}>
            <div className={styles.inputGroup}>
              <label htmlFor="designType">Tipo de Diseno</label>
              <select
                id="designType"
                className={styles.selectField}
                value={designType}
                onChange={(event) => setDesignType(event.target.value as DesignType)}
              >
                <option value="bca">Bloques Completos al Azar (BCA)</option>
                <option value="incomplete">Bloques Incompletos Balanceados</option>
                <option value="lattice">Latice Cuadrado</option>
              </select>
            </div>
            <div className={styles.inputGroup}>
              <label htmlFor="treatments">Numero de Tratamientos</label>
              <input
                id="treatments"
                type="number"
                min="2"
                max="100"
                value={treatments}
                onChange={(event) => setTreatments(event.target.value)}
              />
            </div>
            <div className={styles.inputGroup}>
              <label htmlFor="reps">Numero de Repeticiones</label>
              <input
                id="reps"
                type="number"
                min="2"
                max="10"
                value={reps}
                onChange={(event) => setReps(event.target.value)}
              />
            </div>
          </div>

          <div className={styles.inputRow}>
            <div className={styles.inputGroup}>
              <label htmlFor="cv">CV esperado (%)</label>
              <input
                id="cv"
                type="number"
                min="1"
                max="50"
                step="0.1"
                value={cv}
                onChange={(event) => setCv(event.target.value)}
              />
            </div>
            <div className={styles.inputGroup}>
              <label htmlFor="diff">Diferencia minima detectable (%)</label>
              <input
                id="diff"
                type="number"
                min="1"
                max="50"
                step="0.1"
                value={diff}
                onChange={(event) => setDiff(event.target.value)}
              />
            </div>
            <div className={styles.inputGroup}>
              <label htmlFor="alpha">Nivel de significancia (alpha)</label>
              <input
                id="alpha"
                type="number"
                min="0.01"
                max="0.2"
                step="0.01"
                value={alpha}
                onChange={(event) => setAlpha(event.target.value)}
              />
            </div>
          </div>

          <button type="button" className={styles.calculateButton} onClick={handleGenerate}>
            Generar Diseno Experimental
          </button>
        </div>

        {error && <div className={styles.errorBox}>{error}</div>}

        {result && (
          <div className={styles.resultsSection}>
            <div className={styles.resultSection}>
              <h3>Caracteristicas del Diseno</h3>
              <div className={styles.statsGrid}>
                <div className={styles.statCard}>
                  <h4>Total de Parcelas</h4>
                  <div className={styles.statValue}>{result.totalPlots}</div>
                </div>
                <div className={styles.statCard}>
                  <h4>Numero de Bloques</h4>
                  <div className={styles.statValue}>{result.numBlocks}</div>
                </div>
                <div className={styles.statCard}>
                  <h4>Potencia Estadistica</h4>
                  <div className={styles.statValue}>{(result.power * 100).toFixed(1)}%</div>
                </div>
                <div className={styles.statCard}>
                  <h4>GL del Error</h4>
                  <div className={styles.statValue}>{result.dfError}</div>
                </div>
              </div>
            </div>

            <div className={styles.resultSection}>
              <h3>Croquis de Campo</h3>
              <div className={styles.layoutBlocks}>
                {result.layoutBlocks.map((block) => (
                  <div key={block.title} className={styles.layoutBlock}>
                    <div className={styles.blockHeader}>{block.title}</div>
                    <div
                      className={styles.fieldLayout}
                      style={{ gridTemplateColumns: `repeat(${block.columns}, minmax(58px, 1fr))` }}
                    >
                      {block.plots.map((plot, idx) => (
                        <div key={`${block.title}-${idx}`} className={styles.plot}>
                          {plot}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className={styles.resultSection}>
              <h3>Analisis de Potencia</h3>
              {powerCurve && (
                <div className={styles.svgChartWrapper}>
                  <svg viewBox={`0 0 ${powerCurve.width} ${powerCurve.height}`} className={styles.svgChart}>
                    {[0.2, 0.4, 0.6, 0.8, 1].map((tick) => (
                      <g key={`grid-${tick}`}>
                        <line
                          x1={powerCurve.padLeft}
                          y1={powerCurve.y(tick)}
                          x2={powerCurve.width - powerCurve.padRight}
                          y2={powerCurve.y(tick)}
                          className={styles.gridLine}
                        />
                        <text
                          x={powerCurve.padLeft - 8}
                          y={powerCurve.y(tick) + 4}
                          textAnchor="end"
                          className={styles.axisLabel}
                        >
                          {(tick * 100).toFixed(0)}%
                        </text>
                      </g>
                    ))}
                    {powerCurve.points.map((point) => (
                      <g key={`rep-${point.r}`}>
                        <line
                          x1={powerCurve.x(point.r)}
                          y1={powerCurve.height - powerCurve.padBottom}
                          x2={powerCurve.x(point.r)}
                          y2={powerCurve.height - powerCurve.padBottom + 5}
                          className={styles.axisTick}
                        />
                        <text
                          x={powerCurve.x(point.r)}
                          y={powerCurve.height - powerCurve.padBottom + 20}
                          textAnchor="middle"
                          className={styles.axisLabel}
                        >
                          {point.r}
                        </text>
                      </g>
                    ))}
                    <line
                      x1={powerCurve.padLeft}
                      y1={powerCurve.height - powerCurve.padBottom}
                      x2={powerCurve.width - powerCurve.padRight}
                      y2={powerCurve.height - powerCurve.padBottom}
                      className={styles.axisLine}
                    />
                    <line
                      x1={powerCurve.padLeft}
                      y1={24}
                      x2={powerCurve.padLeft}
                      y2={powerCurve.height - powerCurve.padBottom}
                      className={styles.axisLine}
                    />
                    <line
                      x1={powerCurve.padLeft}
                      y1={powerCurve.y80}
                      x2={powerCurve.width - powerCurve.padRight}
                      y2={powerCurve.y80}
                      className={styles.referenceLine}
                    />
                    <path d={powerCurve.path} className={styles.chartPath} />
                    {powerCurve.points.map((point) => (
                      <circle
                        key={`point-${point.r}`}
                        cx={powerCurve.x(point.r)}
                        cy={powerCurve.y(point.p)}
                        r={4}
                        className={styles.chartPoint}
                      />
                    ))}
                  </svg>
                </div>
              )}
            </div>

            <div className={styles.infoBox}>
              <h4>Interpretacion</h4>
              <p>{result.interpretation}</p>
            </div>
          </div>
        )}

        <div className={styles.exampleSection}>
          <h4>Ejemplos de uso</h4>
          <p>
            Ensayo de soja: BCA con 15 cultivares y 4 repeticiones para detectar diferencias de 12%
            con CV=18%.
          </p>
          <p>
            Ensayo de trigo: latice 7x7 para 49 lineas y 2 repeticiones cuando hay muchos
            tratamientos.
          </p>
          <p>
            Evaluacion de hibridos: bloques incompletos con 30 hibridos, 3 repeticiones y bloques
            de 6.
          </p>
        </div>
      </div>
    </div>
  );
};
